/** Curated palette — matches the board status token hues. */
export const STAGE_COLOR_PRESETS = [
  "#3B82F6",
  "#8B5CF6",
  "#F59E0B",
  "#22C55E",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#6366F1",
  "#84CC16",
  "#F97316",
];

/** Pastel background/border pairs for kanban swimlanes and gallery groups. */
export const SWIMLANE_TINT_PRESETS = [
  { background: "#fee2e2", border: "#fecaca" },
  { background: "#ffedd5", border: "#fed7aa" },
  { background: "#fef3c7", border: "#fde68a" },
  { background: "#fef9c3", border: "#fef08a" },
  { background: "#ecfccb", border: "#d9f99d" },
  { background: "#dcfce7", border: "#bbf7d0" },
  { background: "#d1fae5", border: "#a7f3d0" },
  { background: "#ccfbf1", border: "#99f6e4" },
  { background: "#cffafe", border: "#a5f3fc" },
  { background: "#e0f2fe", border: "#bae6fd" },
  { background: "#dbeafe", border: "#bfdbfe" },
  { background: "#e0e7ff", border: "#c7d2fe" },
  { background: "#ede9fe", border: "#ddd6fe" },
  { background: "#f3e8ff", border: "#e9d5ff" },
  { background: "#fae8ff", border: "#f5d0fe" },
  { background: "#fce7f3", border: "#fbcfe8" },
  { background: "#f5f5f5", border: "#e5e5e5" },
  { background: "#e5e7eb", border: "#d1d5db" },
];

const SHORT_HEX = /^#([0-9A-Fa-f]{3})$/;
const FULL_HEX = /^#([0-9A-Fa-f]{6})$/;

/** Normalize user input to `#RRGGBB`, or null when invalid / empty. */
export const normalizeStageColor = (value) => {
  if (value == null || value === "") return null;
  const trimmed = String(value).trim();
  if (trimmed === "") return null;

  const full = trimmed.match(FULL_HEX);
  if (full) return `#${full[1].toUpperCase()}`;

  const short = trimmed.match(SHORT_HEX);
  if (short) {
    const [r, g, b] = short[1].split("");
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return null;
};

/** Map an ordered option list to `{ [option]: { background, border } }`. */
export function buildSwimlaneColors(options = [], presets = SWIMLANE_TINT_PRESETS) {
  const colors = {};
  options.forEach((option, index) => {
    colors[option] = presets[index % presets.length];
  });
  return colors;
}

function blendWithWhite(hex, whiteMix) {
  const rgb = parseHexRgb(hex);
  if (!rgb) return hex;
  const mix = (channel) => Math.round(channel + (255 - channel) * whiteMix);
  const toHex = (channel) => mix(channel).toString(16).padStart(2, "0");
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`.toUpperCase();
}

/** Derive a swimlane tint pair from a custom hex accent color. */
export function buildTintFromHex(hex) {
  const base = normalizeStageColor(hex);
  if (!base) return null;
  return {
    background: blendWithWhite(base, 0.82),
    border: blendWithWhite(base, 0.58),
  };
}

export function isTintColorEqual(a, b) {
  if (!a && !b) return true;
  if (!a?.background || !b?.background) return false;
  return (
    normalizeStageColor(a.background) === normalizeStageColor(b.background) &&
    normalizeStageColor(a.border || a.background) ===
      normalizeStageColor(b.border || b.background)
  );
}

const DARK_TEXT = "#18181b";
const LIGHT_TEXT = "#fafafa";

function parseHexRgb(hex) {
  const normalized = normalizeStageColor(hex);
  if (!normalized) return null;
  const value = normalized.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

/** Pick readable label text for a solid or pastel background. */
export function getContrastTextColor(backgroundColor, {
  darkText = DARK_TEXT,
  lightText = LIGHT_TEXT,
} = {}) {
  const rgb = parseHexRgb(backgroundColor);
  if (!rgb) return darkText;

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.62 ? darkText : lightText;
}

/** Inline styles for swimlane chips that use fixed light/dark tint backgrounds. */
export function getTintedSwimlaneChipStyles({ background, border }) {
  if (!background) return null;

  const textColor = getContrastTextColor(background);
  const isLightBackground = textColor === DARK_TEXT;

  return {
    backgroundColor: background,
    borderColor: border || background,
    color: textColor,
    removeBg: isLightBackground ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.16)",
    removeColor: isLightBackground ? "rgba(0, 0, 0, 0.55)" : "rgba(255, 255, 255, 0.88)",
    removeHoverBg: isLightBackground ? "rgba(0, 0, 0, 0.14)" : "rgba(255, 255, 255, 0.24)",
    removeHoverColor: isLightBackground ? "#dc2626" : "#fca5a5",
    swatchFrame: isLightBackground ? "rgba(255, 255, 255, 0.92)" : "rgba(0, 0, 0, 0.28)",
    swatchRing: isLightBackground ? "rgba(0, 0, 0, 0.22)" : "rgba(255, 255, 255, 0.45)",
  };
}

/** Visible mini-swatch styles when the chip fill would blend into the chip body. */
export function getSwimlaneColorSwatchStyles(chipColor, tintedStyles) {
  if (!chipColor?.background) return null;

  return {
    backgroundColor: chipColor.background,
    borderColor: chipColor.border || chipColor.background,
    frame: tintedStyles?.swatchFrame || "rgba(255, 255, 255, 0.92)",
    ring: tintedStyles?.swatchRing || "rgba(0, 0, 0, 0.22)",
  };
}
